import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ADAPTER_ORDER, adapterBlurb, adapterName } from '@/components/Integrations/adapters/copy';
import { getAdapterMeta } from '@/components/Integrations/adapters/registry';
import {
  capabilitiesFor,
  useIntegrationCapabilities,
} from '@/components/Integrations/capabilities';
import { Page } from '@/components/Ui/Page';
import { PageHeader } from '@/components/Ui/PageHeader';
import { Section } from '@/components/Ui/Section';
import { useUndoToast } from '@/components/Ui/Toast';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { useRustyState } from '@/state';
import { IntegrationEditor } from './IntegrationEditor';
import classes from './Integrations.module.css';
import { SigningSecret } from './SigningSecret';

export function NewIntegrationScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createLanguageURL } = useLanguageAwareRouting();
  const [params, setParams] = useSearchParams();
  const toast = useUndoToast();
  const caps = useIntegrationCapabilities();
  const [secret, setSecret] = useState<{ id: string; value: string; kind: string } | null>(null);

  const requested = params.get('kind');
  const kind = requested && getAdapterMeta(requested) ? requested : null;
  if (secret && secret.kind !== kind) setSecret(null);
  const pick = (k: string | null) => setParams(k ? { kind: k } : {}, { replace: false });
  const toList = () => navigate(createLanguageURL('/account/integrations'));

  const title = secret
    ? t('integrations.signing.title')
    : kind
      ? t('integrations.editor.newTitle', { name: adapterName(kind, t) })
      : t('integrations.newTitle');

  return (
    <Page width="narrow">
      <PageHeader
        title={title}
        breadcrumb={[{ label: t('integrations.pageTitle'), to: '/account/integrations' }]}
      />

      {secret ? (
        <Section title={t('integrations.signing.sectionTitle')}>
          <SigningSecret
            secret={secret.value}
            signatureHeader={capabilitiesFor(caps, secret.kind).signature_header}
            onDone={toList}
          />
        </Section>
      ) : kind ? (
        <Section title={adapterName(kind, t)} subtitle={adapterBlurb(kind, t)}>
          <IntegrationEditor
            kind={kind}
            mode="create"
            onBack={() => pick(null)}
            onCreate={async (body) => {
              const created = await useRustyState
                .getState()
                .api.newIntegration(body, { onSuccess: false });
              if (created.hmac_secret_b64) {
                setSecret({ id: created.id, value: created.hmac_secret_b64, kind });
                return;
              }
              toast(t('integrations.editor.created', { title: created.title }));
              toList();
            }}
          />
        </Section>
      ) : (
        <Section
          title={t('integrations.picker.title')}
          subtitle={t('integrations.picker.subtitle')}
        >
          <div className={classes.grid}>
            {ADAPTER_ORDER.filter((k) => getAdapterMeta(k)).map((k) => (
              <button key={k} type="button" className={classes.adapter} onClick={() => pick(k)}>
                <strong>{adapterName(k, t)}</strong>
                <span className={classes.help}>{adapterBlurb(k, t)}</span>
              </button>
            ))}
          </div>
        </Section>
      )}
    </Page>
  );
}
